import { classifyScannedCode, normalizeLabelCode, parseSellerItemRef } from '@barso/shared';
import { normalizeText, type IndexEntry } from './catalogIndex';

/**
 * Suche im Artikelstand der Kasse (U17b), rein und ohne Netz. Die Eingabe läuft für die Code-Erkennung immer zuerst durch
 * `classifyScannedCode` (CLAUDE.md Regel 4). Rangfolge:
 *
 * 1. BARSO-Code oder fremder Code: genau dieser Artikel.
 * 2. `17/5`, `17-5`, `17 5`: Verkäufer 17, Artikel 5.
 * 3. 1 bis 4 Ziffern: alle Artikel von Verkäufer n; gemischt (`17 jacke`): Verkäufer 17 und „jacke“ in der Bezeichnung.
 * 4. ab 4 Ziffern: der Code enthält die Ziffern (halb lesbares Etikett), ohne Trennzeichen verglichen.
 * 5./6. Text: jedes Wort steckt in Bezeichnung oder Größe (UND); Wortanfang (5) vor Wortmitte (6).
 *
 * Ergibt eine Stufe nichts, gilt die nächste. Innerhalb eines Rangs steht verkäuflich vor verkauft, nicht angenommen oder
 * gelöscht, danach Verkäufernummer und Artikelnummer.
 */

export const MAX_HITS = 50;

export type SearchKind = 'empty' | 'code' | 'ref' | 'seller' | 'mixed' | 'fragment' | 'text';

export interface SearchResult {
  kind: SearchKind;
  /** Höchstens `max` Treffer, in Rangfolge. */
  hits: readonly IndexEntry[];
  /** Alle Treffer (auch die nicht angezeigten). */
  total: number;
  /** Zahl der nicht angezeigten Treffer („n weitere: bitte genauer suchen“). */
  more: number;
}

/** Verkäuflich: angenommen und nicht gelöscht. */
export function sellable(entry: IndexEntry): boolean {
  return entry.status === 'received' && !entry.deleted;
}

interface Ranked {
  entry: IndexEntry;
  rank: number;
}

/** Sortierschlüssel als eine Zahl (schnell auch bei 25.000 Treffern): Rang, verkäuflich, Verkäufer, Artikelnummer. */
function keyOf({ entry, rank }: Ranked): number {
  const itemNo = Math.min(entry.itemNo ?? 9_999, 9_999);
  return (
    ((rank * 2 + (sellable(entry) ? 0 : 1)) * 100_000 + Math.min(entry.sellerNumber, 99_999)) *
      10_000 +
    itemNo
  );
}

function finish(kind: SearchKind, ranked: Ranked[], max: number): SearchResult {
  const keyed = ranked.map((entry) => ({ entry, key: keyOf(entry) }));
  keyed.sort((x, y) => x.key - y.key);
  const hits = keyed.slice(0, max).map((item) => item.entry.entry);
  return { kind, hits, total: ranked.length, more: ranked.length - hits.length };
}

const isDigits = (text: string): boolean => /^\d+$/.test(text);

/** Wortanfang: das Wort steht am Anfang der Bezeichnung oder nach einem Leerzeichen. */
function atWordStart(norm: string, token: string): boolean {
  return norm.startsWith(token) || norm.includes(` ${token}`);
}

/** UND über alle Wörter; Rang 5, wenn jedes Wort an einem Wortanfang steht, sonst 6. */
function textHits(entries: readonly IndexEntry[], tokens: readonly string[]): Ranked[] {
  const ranked: Ranked[] = [];
  for (const entry of entries) {
    let ok = true;
    let starts = true;
    for (const token of tokens) {
      if (!entry.norm.includes(token)) {
        ok = false;
        break;
      }
      if (starts && !atWordStart(entry.norm, token)) starts = false;
    }
    if (ok) ranked.push({ entry, rank: starts ? 5 : 6 });
  }
  return ranked;
}

export function searchCatalog(
  entries: readonly IndexEntry[],
  raw: string,
  max: number = MAX_HITS,
): SearchResult {
  const query = raw.trim();
  if (query === '') return { kind: 'empty', hits: [], total: 0, more: 0 };

  // 1. Code (BARSO-Code oder fremder Code)
  const scanned = classifyScannedCode(query);
  if (scanned !== null) {
    const exact: Ranked[] = [];
    for (const entry of entries) {
      if (scanned.kind === 'label' ? entry.code === scanned.code : entry.altCode === scanned.code) {
        exact.push({ entry, rank: 1 });
      }
    }
    if (exact.length > 0) return finish('code', exact, max);
    // Ein vollständiger BARSO-Code ist nichts anderes: ohne Treffer ist er unbekannt
    if (scanned.kind === 'label') return { kind: 'code', hits: [], total: 0, more: 0 };
  }

  // 2. Verkäufer und Artikel: 17/5, 17-5, 17 5
  const ref = parseSellerItemRef(query);
  if (ref !== null) {
    const found: Ranked[] = [];
    for (const entry of entries) {
      if (entry.sellerNumber === ref.sellerNumber && entry.itemNo === ref.itemNo) {
        found.push({ entry, rank: 2 });
      }
    }
    if (found.length > 0) return finish('ref', found, max);
  }

  // 3. und 4. nur Ziffern (Trennzeichen zählen nicht): Verkäufer n, Code-Bruchstück
  const compact = normalizeLabelCode(query);
  if (isDigits(compact)) {
    const found: Ranked[] = [];
    const seen = new Set<string>();
    if (compact.length <= 4) {
      const seller = Number(compact);
      for (const entry of entries) {
        if (entry.sellerNumber === seller) {
          found.push({ entry, rank: 3 });
          seen.add(entry.id);
        }
      }
    }
    if (compact.length >= 4) {
      for (const entry of entries) {
        if (seen.has(entry.id)) continue;
        if (entry.code.includes(compact) || entry.altDigits.includes(compact)) {
          found.push({ entry, rank: 4 });
        }
      }
    }
    if (found.length > 0) {
      return finish(
        compact.length <= 4 && found.some((f) => f.rank === 3) ? 'seller' : 'fragment',
        found,
        max,
      );
    }
  }

  // 3. gemischt: eine Zahl (Verkäufer) und Wörter
  const tokens = normalizeText(query).split(' ');
  const numbers = tokens.filter((token) => isDigits(token) && token.length <= 5);
  const words = tokens.filter((token) => !isDigits(token));
  if (numbers.length === 1 && words.length > 0 && numbers.length + words.length === tokens.length) {
    const seller = Number(numbers[0]);
    const found = textHits(
      entries.filter((entry) => entry.sellerNumber === seller),
      words,
    ).map((entry) => ({ entry: entry.entry, rank: 3 }));
    if (found.length > 0) return finish('mixed', found, max);
  }

  // 5. und 6. Text (Ziffern zählen hier als Wörter, z. B. eine Größe)
  return finish('text', textHits(entries, tokens), max);
}
